import React, { useState, useRef } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

const PLACES_AUTOCOMPLETE_URL =
  "https://places.googleapis.com/v1/places:autocomplete";
const PLACES_DETAILS_URL = "https://places.googleapis.com/v1/"; // + places/{place_id}
const PLACES_TEXT_SEARCH_URL = "https://places.googleapis.com/v1/places:searchText";
const API_KEY = process.env.REACT_APP_PLACES_API_KEY;

export const CustomAddressAutocomplete = ({
  label,
  addressSearchQuery,
  setAddressSearchQuery,
  setAddressComponents,
  addressValidationError,
  setAddressValidationError,
  placeholder = "Start typing your address...",
  localStorageLabel,
  isRequired,
  onResolvingChange,
  disabled = false,
}) => {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const sessionTokenRef = useRef(null);

  const removeAddressComponentsFromLocalstorage = () => {
    if (localStorage.getItem(localStorageLabel) !== null) {
      localStorage.removeItem(localStorageLabel);
    }
  };

  // Generate a session token for each session
  const getSessionToken = () => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = Math.random().toString(36).substring(2);
    }
    return sessionTokenRef.current;
  };

  // Fetch predictions from REST API
  const fetchPredictions = async (input) => {
    setLoading(true);
    setPredictions([]);
    try {
      const res = await fetch(PLACES_AUTOCOMPLETE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": API_KEY,
          "X-Goog-FieldMask":
            "suggestions.placePrediction.placeId,suggestions.placePrediction.text",
        },
        body: JSON.stringify({
          input,
          sessionToken: getSessionToken(),
          includedRegionCodes: ["us"],
        }),
      });
      const data = await res.json();
      const suggestions = (data.suggestions || [])
        .map((s) => s.placePrediction)
        .filter(Boolean);
      setPredictions(suggestions);
    } catch (err) {
      setAddressValidationError("Failed to fetch predictions.");
    } finally {
      setLoading(false);
    }
  };

  // Google's autocomplete occasionally suggests a place whose ID its own
  // details endpoint then rejects (404 "The provided Place ID is no longer
  // valid" — e.g. "123 William Street, New York"). Looking the same text up
  // via Text Search returns a fresh, valid place for it.
  const searchPlaceByText = async (text) => {
    const res = await fetch(PLACES_TEXT_SEARCH_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": API_KEY,
        "X-Goog-FieldMask": "places.formattedAddress,places.addressComponents",
      },
      body: JSON.stringify({ textQuery: text, regionCode: "us", pageSize: 1 }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.places?.[0] || null;
  };

  // Fetch place details from REST API
  const fetchPlaceDetails = async (placeId, selectedText) => {
    // Selecting a suggestion only fills in the text immediately; the
    // structured address components (what validation actually checks)
    // don't land until this request resolves. Signal "resolving" so the
    // parent step can hold off on Continue instead of racing this fetch.
    onResolvingChange?.(true);
    try {
      const res = await fetch(
        `${PLACES_DETAILS_URL}places/${placeId}?fields=formattedAddress,addressComponents`,
        {
          headers: {
            "X-Goog-Api-Key": API_KEY,
            "X-Goog-FieldMask": "formattedAddress,addressComponents",
          },
        }
      );
      // fetch() doesn't throw on an HTTP error, so check the status —
      // otherwise an error body (no formattedAddress) silently blanked out
      // the address the borrower had just picked.
      let place = res.ok ? await res.json() : null;
      if (!place?.formattedAddress && selectedText) {
        place = await searchPlaceByText(selectedText);
      }
      if (!place?.formattedAddress) {
        // Keep what they picked on screen, but without components it can't
        // pass validation — say so instead of failing silently.
        setAddressSearchQuery(selectedText || "");
        setAddressComponents([]);
        setAddressValidationError(
          "We couldn't verify this address. Please pick another suggestion or retype it."
        );
        sessionTokenRef.current = null;
        return;
      }
      setAddressSearchQuery(place.formattedAddress);
      setAddressComponents(place.addressComponents || []);
      setAddressValidationError("");
      if (localStorageLabel) {
        localStorage.setItem(
          localStorageLabel,
          JSON.stringify(place.addressComponents || [])
        );
      }
      // Reset session token for next session
      sessionTokenRef.current = null;
    } catch (err) {
      setAddressValidationError("Failed to fetch address details.");
    } finally {
      onResolvingChange?.(false);
    }
  };

  // Handle input change. MUI's Autocomplete fires this with reason "reset"
  // on its own (e.g. re-syncing on mount, since `value` is always kept null
  // while `inputValue` holds text) — not just when the user actually types.
  // Only treat "input"/"clear" as a real edit; a "reset" must never wipe out
  // an already-selected address's components (that's what let a perfectly
  // valid, previously-selected address fail validation again after
  // navigating back to this step and not touching the field).
  const handleInputChange = (event, value, reason) => {
    setAddressSearchQuery(value);
    if (reason !== "input" && reason !== "clear") return;
    setAddressValidationError("");
    setAddressComponents([]);
    removeAddressComponentsFromLocalstorage();
    if (value) {
      fetchPredictions(value);
    } else {
      setPredictions([]);
      sessionTokenRef.current = null;
    }
  };

  // Handle selection
  const handleSelect = (event, value) => {
    if (value && value.placeId) {
      setAddressSearchQuery(value.text.text);
      setPredictions([]);
      fetchPlaceDetails(value.placeId, value.text.text);
    }
  };

  return (
    <div className="pd6-address-field" style={{ width: "100%", position: "relative" }}>
      <label className="form-label">
        {label}
        {isRequired && <span className="pd6-required">*</span>}
      </label>
      <Autocomplete
        freeSolo
        filterOptions={(x) => x}
        options={predictions}
        getOptionLabel={(option) => (option.text ? option.text.text : "")}
        loading={loading}
        disabled={disabled}
        value={null}
        inputValue={addressSearchQuery}
        onInputChange={handleInputChange}
        onChange={handleSelect}
        componentsProps={{
          paper: { className: "pd6-address-dropdown" },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            error={!!addressValidationError}
            helperText={addressValidationError}
            fullWidth
            variant="outlined"
            size="small"
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.placeId}>
            {option.text.text}
          </li>
        )}
      />
    </div>
  );
};
